/**
 * Quiet Hours Editor - Configure notification quiet hours
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface QuietHoursConfig {
  enabled: boolean;
  start: string;
  end: string;
  timezone: string | null;
}

interface QuietHoursEditorProps {
  config: QuietHoursConfig;
  onUpdate: (config: QuietHoursConfig) => void;
}

// Common timezones for quick selection
const COMMON_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Central European (CET)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET)' },
  { value: 'Asia/Tokyo', label: 'Japan (JST)' },
  { value: 'Asia/Shanghai', label: 'China (CST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
  { value: 'UTC', label: 'UTC' },
];

// Generate time options in 30-minute increments
const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const hours = Math.floor(i / 2);
  const minutes = (i % 2) * 30;
  const value = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  const period = hours < 12 ? 'AM' : 'PM';
  const label = `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
  return { value, label };
});

export const QuietHoursEditor: React.FC<QuietHoursEditorProps> = ({
  config,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const [localConfig, setLocalConfig] = useState<QuietHoursConfig>(config);

  useEffect(() => {
    setLocalConfig(config);
  }, [config]);

  const handleToggle = () => {
    const newConfig = { ...localConfig, enabled: !localConfig.enabled };
    setLocalConfig(newConfig);
    onUpdate(newConfig);
  };

  const handleStartChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newConfig = { ...localConfig, start: e.target.value };
    setLocalConfig(newConfig);
    onUpdate(newConfig);
  };

  const handleEndChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newConfig = { ...localConfig, end: e.target.value };
    setLocalConfig(newConfig);
    onUpdate(newConfig);
  };

  const handleTimezoneChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newConfig = { ...localConfig, timezone: e.target.value || null };
    setLocalConfig(newConfig);
    onUpdate(newConfig);
  };

  // Calculate duration for display
  const calculateDuration = (): string => {
    const [startHour = 0, startMin = 0] = localConfig.start.split(':').map(Number);
    const [endHour = 0, endMin = 0] = localConfig.end.split(':').map(Number);

    const startMinutes = (startHour ?? 0) * 60 + (startMin ?? 0);
    let endMinutes = (endHour ?? 0) * 60 + (endMin ?? 0);

    // Handle overnight spans (e.g., 22:00 - 08:00)
    if (endMinutes <= startMinutes) {
      endMinutes += 24 * 60;
    }

    const durationMinutes = endMinutes - startMinutes;
    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;

    if (minutes === 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''}`;
    }
    return `${hours}h ${minutes}m`;
  };

  return (
    <section className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('settings.notifications.quietHours', 'Quiet Hours')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t('settings.notifications.quietHoursDesc', 'Pause notifications during specific hours')}
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={localConfig.enabled}
              onChange={handleToggle}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>

      {localConfig.enabled && (
        <div className="p-6 space-y-6">
          {/* Time Range */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('settings.notifications.quietStart', 'Start Time')}
              </label>
              <select
                value={localConfig.start}
                onChange={handleStartChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {TIME_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('settings.notifications.quietEnd', 'End Time')}
              </label>
              <select
                value={localConfig.end}
                onChange={handleEndChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {TIME_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Duration Display */}
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>
              {t('settings.notifications.quietDuration', 'Duration')}: {calculateDuration()}
            </span>
          </div>

          {/* Timezone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('settings.notifications.timezone', 'Timezone')}
            </label>
            <select
              value={localConfig.timezone || ''}
              onChange={handleTimezoneChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">
                {t('settings.notifications.useProfileTimezone', 'Use profile timezone')}
              </option>
              {COMMON_TIMEZONES.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {t('settings.notifications.timezoneHint', 'Select a timezone or use your profile setting')}
            </p>
          </div>

          {/* Visual Preview */}
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              {t('settings.notifications.quietPreview', 'Preview')}
            </h3>
            <div className="relative h-8 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              {/* 24-hour bar with quiet hours highlighted */}
              <QuietHoursBar start={localConfig.start} end={localConfig.end} />
            </div>
            <div className="flex justify-between mt-1 text-xs text-gray-500 dark:text-gray-400">
              <span>12 AM</span>
              <span>6 AM</span>
              <span>12 PM</span>
              <span>6 PM</span>
              <span>12 AM</span>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex gap-3">
              <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm text-blue-700 dark:text-blue-300">
                <p className="font-medium">
                  {t('settings.notifications.quietHoursInfo', 'How quiet hours work')}
                </p>
                <p className="mt-1 text-blue-600 dark:text-blue-400">
                  {t('settings.notifications.quietHoursInfoDesc', 'During quiet hours, notifications will be silently delivered but won\'t trigger sounds, badges, or push notifications. You\'ll see them when you check the app.')}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// Visual component for the quiet hours bar
const QuietHoursBar: React.FC<{ start: string; end: string }> = ({ start, end }) => {
  const [startHour = 0, startMin = 0] = start.split(':').map(Number);
  const [endHour = 0, endMin = 0] = end.split(':').map(Number);

  const startPercent = (((startHour ?? 0) * 60 + (startMin ?? 0)) / (24 * 60)) * 100;
  const endPercent = (((endHour ?? 0) * 60 + (endMin ?? 0)) / (24 * 60)) * 100;

  // Handle overnight spans
  if (endPercent <= startPercent) {
    // Two segments: start-to-end-of-day and start-of-day-to-end
    return (
      <>
        <div
          className="absolute h-full bg-primary-500 dark:bg-primary-600"
          style={{ left: `${startPercent}%`, right: '0%' }}
        />
        <div
          className="absolute h-full bg-primary-500 dark:bg-primary-600"
          style={{ left: '0%', width: `${endPercent}%` }}
        />
      </>
    );
  }

  return (
    <div
      className="absolute h-full bg-primary-500 dark:bg-primary-600"
      style={{ left: `${startPercent}%`, width: `${endPercent - startPercent}%` }}
    />
  );
};

export default QuietHoursEditor;
